import React, { ForwardedRef, Suspense, useContext } from "react"
import { AuthCodeProps, AuthCodeRef } from "react-auth-code-input"
import { useController } from "react-hook-form"

import classNames from "classnames"

import { WithClassName } from "shared/types/WithClassName"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import { Input as DefaultInput, InputContext, InputRootProps } from "../Input"
import { InputControlWrapper } from "../InputControlWrapper"
import { TextInputErrorMessage } from "../text/components/TextInputErrorMessage"

interface AuthCodeInputProps extends Partial<AuthCodeProps> {
  name?: string
  className?: string
}

interface LoaderProps extends WithClassName {
  readonly?: boolean
  count?: number
}

const LazyAuthCodeInput = React.lazy(() => import("react-auth-code-input"))

function Loader({ readonly, className, count = 6 }: LoaderProps) {
  return (
    <Skeleton
      containerClassName={classNames(
        "input__loader",
        "input__control",
        { readonly },
        className
      )}
      count={count}
    />
  )
}

const InputBase = React.memo(
  React.forwardRef(
    (
      { className, ariaLabel, onChange, ...props }: AuthCodeInputProps,
      externalRef: ForwardedRef<AuthCodeRef>
    ) => {
      const { label, autoFocus } = useContext(InputContext)

      const handleChange = (value: string) => {
        onChange?.(value)
      }

      return (
        <InputControlWrapper Loader={<Loader className={className} />}>
          <Suspense fallback={<Loader className={className} />}>
            <LazyAuthCodeInput
              {...props}
              containerClassName="auth-code-input__container"
              inputClassName="auth-code-input__input"
              ref={externalRef}
              onChange={handleChange}
              autoFocus={autoFocus}
              ariaLabel={ariaLabel || label}
            />
          </Suspense>
        </InputControlWrapper>
      )
    }
  )
)

const HookFormInput = React.memo(
  React.forwardRef(
    (
      { className, ...props }: AuthCodeInputProps,
      externalRef: ForwardedRef<AuthCodeRef>
    ) => {
      const { name, disabled } = useContext(InputContext)
      const { field } = useController({
        name,
        disabled
      })

      return (
        <InputBase
          {...props}
          ref={externalRef}
          className={className}
          onChange={field.onChange}
        />
      )
    }
  )
)

const Input = React.memo(
  React.forwardRef(
    (props: AuthCodeInputProps, externalRef: ForwardedRef<AuthCodeRef>) => {
      const { name } = useContext(InputContext)

      if (name) {
        return <HookFormInput {...props} ref={externalRef} />
      }
      return <InputBase {...props} ref={externalRef} />
    }
  )
)

export const AuthCodeInput = {
  ...DefaultInput,
  Root: DefaultInput.Root as React.FC<InputRootProps>,
  Input,
  ErrorMessage: TextInputErrorMessage
}
